import { ChevronDown, Download, LoaderCircle, PackageOpen } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { OperationalPager } from "@/components/operational/OperationalPager";
import {
  useExportPedidoReports,
  usePedidoReports,
} from "@/features/pedidoReport/hooks";
import {
  createInitialPedidoReportSelection,
  filterPedidoReportSuppliers,
  getPedidoReportCheckboxState,
  getPedidoReportOrderState,
  pedidoReportKey,
  prunePedidoReportSelection,
  reconcilePedidoReportExportSelection,
  setPedidoReportOrderSelected,
  setPedidoReportOrdersSelected,
  pagePedidoReportOrders,
  type PedidoReportCheckboxState,
} from "@/features/pedidoReport/selection";
import type {
  PedidoReportOrder,
  PedidoReportRef,
  PedidoReportResponse,
  PedidoReportSupplier,
} from "@/features/pedidoReport/types";
import { todaySaoPaulo } from "@/lib/dates";
import "./PedidoReportWorkspace.css";

type Toast = {
  tone: "success" | "warning" | "error";
  title: string;
  message?: string;
};

const pedidoReportPageSize = 20;
const pedidoReportExportBatchSize = 10;

type ExportProgress = {
  processed: number;
  total: number;
  part: number;
  parts: number;
};

export function PedidoReportWorkspace() {
  const [date, setDate] = useState(todaySaoPaulo);
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(
    null,
  );
  const [toast, setToast] = useState<Toast | null>(null);
  const initializedDate = useRef<string | null>(null);
  const query = usePedidoReports(date);
  const exportMutation = useExportPedidoReports();
  const suppliers = query.data?.suppliers ?? [];
  const pendingReconciliation = useRef<{
    data: PedidoReportResponse | undefined;
    suppliers: PedidoReportSupplier[];
    selection: Set<string>;
  } | null>(null);

  useEffect(() => {
    if (!query.data) return;
    const pending = pendingReconciliation.current;
    if (pending && query.isSuccess && query.data !== pending.data) {
      pendingReconciliation.current = null;
      setSelection((current) =>
        reconcilePedidoReportExportSelection(
          pending.suppliers,
          query.data.suppliers,
          current,
          pending.selection,
        ),
      );
      return;
    }
    if (initializedDate.current !== date) {
      initializedDate.current = date;
      setSelection(createInitialPedidoReportSelection(query.data.suppliers));
      return;
    }
    setSelection((current) =>
      prunePedidoReportSelection(query.data.suppliers, current),
    );
  }, [date, query.data]);

  const filteredSuppliers = useMemo(
    () => filterPedidoReportSuppliers(suppliers, search),
    [search, suppliers],
  );
  const filteredOrderCount = filteredSuppliers.reduce(
    (total, supplier) => total + supplier.orders.length,
    0,
  );
  const filteredReadyCount = filteredSuppliers.reduce(
    (total, supplier) =>
      total +
      supplier.orders.filter((order) => order.readiness === "ready").length,
    0,
  );
  const pageCount = Math.max(
    1,
    Math.ceil(filteredOrderCount / pedidoReportPageSize),
  );
  const currentPage = Math.min(page, pageCount - 1);
  const visibleSuppliers = useMemo(
    () =>
      pagePedidoReportOrders(
        filteredSuppliers,
        currentPage,
        pedidoReportPageSize,
      ),
    [filteredSuppliers, currentPage],
  );
  const allOrders = suppliers.flatMap((supplier) => supplier.orders);
  const readyOrders = allOrders.filter((order) => order.readiness === "ready");
  const eligibleSelection = useMemo(
    () => prunePedidoReportSelection(suppliers, selection),
    [selection, suppliers],
  );
  const waitingCount = allOrders.length - readyOrders.length;
  const globalSelectionState = checkboxState(
    eligibleSelection.size,
    readyOrders.length,
  );
  const filteredSelectedCount = filteredSuppliers.reduce(
    (total, supplier) =>
      total +
      supplier.orders.filter((order) =>
        eligibleSelection.has(
          pedidoReportKey(supplier.supplier_id, order.order_id),
        ),
      ).length,
    0,
  );
  const filteredSelectionState = checkboxState(
    filteredSelectedCount,
    filteredReadyCount,
  );
  const selectedSupplierCount = suppliers.filter((supplier) =>
    supplier.orders.some((order) =>
      eligibleSelection.has(
        pedidoReportKey(supplier.supplier_id, order.order_id),
      ),
    ),
  ).length;

  async function handleExport() {
    if (isExporting) return;
    const refs = selectedRefs(suppliers, eligibleSelection);
    if (!refs.length) return;
    const batches = chunkRefs(refs, pedidoReportExportBatchSize);
    let successCount = 0;
    let failureCount = 0;
    let downloadedFiles = 0;
    let lastError: unknown;
    const rejectedSelection = new Set<string>();
    setIsExporting(true);
    setExportProgress({
      processed: 0,
      total: refs.length,
      part: 1,
      parts: batches.length,
    });
    setToast(null);
    try {
      for (const [index, batch] of batches.entries()) {
        try {
          const result = await exportMutation.mutateAsync({
            date,
            orders: batch,
          });
          downloadBlob(
            result.blob,
            batches.length > 1
              ? batchFilename(date, index + 1, batches.length, batch.length)
              : (result.filename ?? defaultFilename(date, batch.length)),
          );
          downloadedFiles++;
          successCount += result.successCount;
          failureCount += result.failureCount;
        } catch (error) {
          lastError = error;
          failureCount += batch.length;
          batch.forEach((ref) =>
            rejectedSelection.add(
              pedidoReportKey(ref.supplier_id, ref.order_id),
            ),
          );
        }
        setExportProgress({
          processed: Math.min(
            (index + 1) * pedidoReportExportBatchSize,
            refs.length,
          ),
          total: refs.length,
          part: Math.min(index + 2, batches.length),
          parts: batches.length,
        });
      }
      pendingReconciliation.current = {
        data: query.data,
        suppliers,
        selection: rejectedSelection,
      };
      const refreshed = await query.refetch();
      if (refreshed.isSuccess && refreshed.data) {
        pendingReconciliation.current = null;
        setSelection(
          reconcilePedidoReportExportSelection(
            suppliers,
            refreshed.data.suppliers,
            eligibleSelection,
            rejectedSelection,
          ),
        );
      }
      setToast(
        failureCount
          ? {
              tone: "warning",
              title: `${successCount} de ${refs.length} documentos gerados`,
              message: `${failureCount} falharam e continuam selecionados para nova tentativa.${lastError ? ` ${getUserFacingErrorMessage(lastError, "Tente novamente.")}` : ""}`,
            }
          : {
              tone: "success",
              title:
                refs.length === 1
                  ? "PDF gerado"
                  : downloadedFiles === 1
                    ? "1 arquivo gerado"
                    : `${downloadedFiles} arquivos gerados`,
              message: `${successCount} documentos prontos para envio.`,
            },
      );
    } catch (error) {
      setToast({
        tone: "error",
        title: "Não foi possível gerar os pedidos",
        message: getUserFacingErrorMessage(
          error,
          "Confira a seleção e tente novamente.",
        ),
      });
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  }

  return (
    <section
      className="pedido-report-workspace"
      aria-label="Pedidos por fornecedor"
    >
      <div className="pedido-report-toolbar">
        <OperationalSearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(0);
          }}
          placeholder="Buscar fornecedor, cliente, loja ou pedido"
          ariaLabel="Buscar pedidos para relatório"
        />
        <label className="pedido-report-date">
          <span>Data da cotação</span>
          <input
            type="date"
            value={date}
            disabled={isExporting}
            onChange={(event) => {
              initializedDate.current = null;
              pendingReconciliation.current = null;
              setDate(event.target.value);
              setSearch("");
              setPage(0);
            }}
          />
        </label>
      </div>

      <div className="pedido-report-metrics" aria-label="Resumo dos pedidos">
        <Metric value={readyOrders.length} label="Prontos" />
        <Metric value={waitingCount} label="Aguardando" />
        <Metric value={suppliers.length} label="Fornecedores" />
      </div>

      <div className="pedido-report-table">
        <div className="pedido-report-select-all">
          <div className="pedido-report-selection-option">
            <SelectionCheckbox
              state={globalSelectionState}
              disabled={!readyOrders.length || isExporting}
              ariaLabel="Selecionar todos os pedidos prontos"
              onChange={(checked) =>
                setSelection(
                  setPedidoReportOrdersSelected(
                    eligibleSelection,
                    suppliers,
                    checked,
                  ),
                )
              }
            />
            <span>Todos os pedidos prontos</span>
            <strong>{eligibleSelection.size} PDFs</strong>
          </div>
          {search.trim() ? (
            <div className="pedido-report-selection-option">
              <SelectionCheckbox
                state={filteredSelectionState}
                disabled={!filteredReadyCount || isExporting}
                ariaLabel="Selecionar resultados da busca"
                onChange={(checked) =>
                  setSelection(
                    setPedidoReportOrdersSelected(
                      eligibleSelection,
                      filteredSuppliers,
                      checked,
                    ),
                  )
                }
              />
              <span>Resultados da busca</span>
              <strong>{filteredReadyCount}</strong>
            </div>
          ) : null}
        </div>

        <OperationalQueryState
          hasData={filteredSuppliers.length > 0}
          isLoading={query.isLoading}
          isFetching={query.isFetching}
          error={query.error}
          isFiltered={Boolean(search.trim())}
          onRetry={() => void query.refetch()}
          renderSkeleton={() => <PedidoReportSkeleton />}
          renderEmpty={({ filtered }) => (
            <div className="pedido-report-empty">
              <PackageOpen size={28} aria-hidden="true" />
              <strong>
                {filtered ? "Nenhum resultado" : "Nenhum pedido nesta data"}
              </strong>
              <span>
                {filtered
                  ? "Tente outro nome, loja ou número de pedido."
                  : "Quando houver pedidos, eles aparecerão aqui."}
              </span>
            </div>
          )}
        >
          <div
            key={`${search}:${currentPage}`}
            className="pedido-report-ledger"
          >
            {visibleSuppliers.map((filteredSupplier) => {
              const supplier =
                suppliers.find(
                  (item) => item.supplier_id === filteredSupplier.supplier_id,
                ) ?? filteredSupplier;
              return (
                <SupplierGroup
                  key={supplier.supplier_id}
                  supplier={supplier}
                  visibleOrders={filteredSupplier.orders}
                  expandForSearch={Boolean(search.trim())}
                  compactByDefault={allOrders.length > pedidoReportPageSize}
                  selection={eligibleSelection}
                  disabled={isExporting}
                  onSelectionChange={setSelection}
                />
              );
            })}
          </div>
          <OperationalPager
            variant="numbered"
            page={currentPage}
            pageCount={pageCount}
            totalItems={filteredOrderCount}
            pageSize={pedidoReportPageSize}
            onPageChange={setPage}
            ariaLabel="Paginação dos pedidos para relatório"
            itemLabel="pedido"
            itemLabelPlural="pedidos"
            className="pedido-report-pager"
          />
        </OperationalQueryState>
      </div>

      {eligibleSelection.size > 0 || isExporting ? (
        <div className="pedido-report-action" aria-live="polite">
          <div>
            <strong>
              {isExporting && exportProgress
                ? `Gerando PDFs · ${Math.round((exportProgress.processed / exportProgress.total) * 100)}%`
                : `${eligibleSelection.size} ${eligibleSelection.size === 1 ? "PDF selecionado" : "PDFs selecionados"}`}
            </strong>
            <span>
              {isExporting && exportProgress
                ? `${exportProgress.processed} de ${exportProgress.total} processados · lote ${exportProgress.part} de ${exportProgress.parts}`
                : `${selectedSupplierCount} fornecedores`}
            </span>
          </div>
          <button
            type="button"
            disabled={isExporting}
            onClick={() => void handleExport()}
          >
            {isExporting ? (
              <LoaderCircle
                className="pedido-report-spinner"
                size={17}
                aria-hidden="true"
              />
            ) : (
              <Download size={17} aria-hidden="true" />
            )}
            {isExporting
              ? "Gerando…"
              : eligibleSelection.size === 1
                ? "Baixar PDF"
                : `Baixar ${Math.ceil(eligibleSelection.size / pedidoReportExportBatchSize)} ${Math.ceil(eligibleSelection.size / pedidoReportExportBatchSize) === 1 ? "ZIP" : "arquivos"}`}
          </button>
          {isExporting && exportProgress ? (
            <div
              className="pedido-report-progress"
              role="progressbar"
              aria-label="Progresso da geração de pedidos"
              aria-valuemin={0}
              aria-valuemax={exportProgress.total}
              aria-valuenow={exportProgress.processed}
            >
              <span
                style={{
                  width: `${(exportProgress.processed / exportProgress.total) * 100}%`,
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {toast ? (
        <FloatingToast
          tone={toast.tone}
          title={toast.title}
          message={toast.message}
          onDismiss={() => setToast(null)}
        />
      ) : null}
    </section>
  );
}

function SupplierGroup({
  supplier,
  visibleOrders,
  expandForSearch,
  compactByDefault,
  selection,
  disabled,
  onSelectionChange,
}: {
  supplier: PedidoReportSupplier;
  visibleOrders: PedidoReportOrder[];
  expandForSearch: boolean;
  compactByDefault: boolean;
  selection: ReadonlySet<string>;
  disabled: boolean;
  onSelectionChange: (selection: Set<string>) => void;
}) {
  const [isOpen, setIsOpen] = useState(!compactByDefault || expandForSearch);
  const contentId = useId();
  useEffect(() => {
    if (expandForSearch) setIsOpen(true);
  }, [expandForSearch]);
  const state = getPedidoReportCheckboxState(selection, supplier);
  const readyCount = supplier.orders.filter(
    (order) => order.readiness === "ready",
  ).length;
  return (
    <section className="pedido-report-supplier">
      <header className="pedido-report-supplier__header">
        <SelectionCheckbox
          state={state}
          disabled={!readyCount || disabled}
          ariaLabel={`Selecionar pedidos de ${supplier.supplier_name}`}
          onChange={(checked) =>
            onSelectionChange(
              setPedidoReportOrdersSelected(selection, [supplier], checked),
            )
          }
        />
        <h2 className="pedido-report-supplier__heading">
          <button
            type="button"
            className="pedido-report-supplier__toggle"
            aria-expanded={isOpen}
            aria-controls={contentId}
            onClick={() => setIsOpen((current) => !current)}
          >
            <span className="pedido-report-supplier__identity">
              <span className="pedido-report-supplier__name">
                {supplier.supplier_name}
              </span>
              <span>
                {supplier.orders.length}{" "}
                {supplier.orders.length === 1 ? "pedido" : "pedidos"}
              </span>
            </span>
            <span className="pedido-report-supplier__summary">
              <strong>
                {readyCount} de {supplier.orders.length} prontos
              </strong>
              <ChevronDown size={18} aria-hidden="true" />
            </span>
          </button>
        </h2>
      </header>
      <div
        className="pedido-report-orders-collapse"
        data-open={isOpen}
        id={contentId}
        inert={!isOpen}
        aria-hidden={!isOpen}
      >
        <div className="pedido-report-orders">
          {visibleOrders.map((order) => {
            const key = pedidoReportKey(supplier.supplier_id, order.order_id);
            const orderState = getPedidoReportOrderState(order);
            return (
              <div
                key={order.order_id}
                className="pedido-report-order"
                data-state={orderState}
                data-selected={selection.has(key) || undefined}
              >
                <SelectionCheckbox
                  state={selection.has(key) ? "all" : "none"}
                  disabled={order.readiness === "waiting" || disabled}
                  ariaLabel={`Selecionar ${order.client_name}, ${order.store_name}`}
                  onChange={(checked) =>
                    onSelectionChange(
                      setPedidoReportOrderSelected(
                        selection,
                        supplier.supplier_id,
                        order,
                        checked,
                      ),
                    )
                  }
                />
                <span className="pedido-report-order__identity">
                  <strong>{order.client_name}</strong>
                  <span>{order.store_name}</span>
                </span>
                <span className="pedido-report-order__number">
                  {order.order_number}
                </span>
                <span className="pedido-report-order__state">{orderState}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SelectionCheckbox({
  state,
  disabled,
  ariaLabel,
  onChange,
}: {
  state: PedidoReportCheckboxState;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <label className="pedido-report-checkbox-target" data-state={state}>
      <input
        ref={ref}
        className="pedido-report-checkbox"
        type="checkbox"
        checked={state === "all"}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

function Metric({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function PedidoReportSkeleton() {
  return (
    <div
      className="pedido-report-skeleton"
      aria-label="Carregando pedidos"
      role="status"
    >
      <span />
      <span />
      <span />
    </div>
  );
}

function checkboxState(
  selected: number,
  total: number,
): PedidoReportCheckboxState {
  if (!selected || !total) return "none";
  return selected === total ? "all" : "some";
}

function selectedRefs(
  suppliers: PedidoReportSupplier[],
  selection: ReadonlySet<string>,
): PedidoReportRef[] {
  return suppliers.flatMap((supplier) =>
    supplier.orders
      .filter((order) =>
        selection.has(pedidoReportKey(supplier.supplier_id, order.order_id)),
      )
      .map((order) => ({
        supplier_id: supplier.supplier_id,
        order_id: order.order_id,
      })),
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function defaultFilename(date: string, count: number) {
  return count === 1 ? `pedido-${date}.pdf` : `pedidos-${date}.zip`;
}

function batchFilename(
  date: string,
  part: number,
  total: number,
  count: number,
) {
  return `${count === 1 ? "pedido" : "pedidos"}-${date}-parte-${part}-de-${total}.${count === 1 ? "pdf" : "zip"}`;
}

function chunkRefs(refs: PedidoReportRef[], size: number): PedidoReportRef[][] {
  const batches: PedidoReportRef[][] = [];
  for (let index = 0; index < refs.length; index += size) {
    batches.push(refs.slice(index, index + size));
  }
  return batches;
}
